import React, { useContext, useEffect, useState } from "react";
import { SettingsContext } from "../../../context/SettingsProvider";
import DeleteModal from "../../../components/modals/DeleteModal";
import SuccessNotification from "../../../components/toast/SuccessNotification";
import ErrorNotification from "../../../components/toast/ErrorNotification";

const VendorTypes = () => {
  const { vendorTypes, setVendorTypes, getVendorTypes, postVendorTypes, updateVendorType, deleteVendorType } = useContext(SettingsContext);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedFeedTypeIndex, setSelectedFeedTypeIndex] = useState(null);
  const [newVendorType, setNewVendorType] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [text, setText] = useState("");

  const closeNotification = () => {
    setShowSuccess(false);
    setShowError(false);
  }

  const addNewVendorType = () => {
    if (newVendorType.trim() !== "") {
      const newVendorTypeObject = {
        type: newVendorType,
        visible: true,
      };
      postVendorTypes(newVendorTypeObject)
        .then(() => {
          setNewVendorType("");
          setShowSuccess(true);
          setText('Successfully added a new feed type.');
        })
        .catch((error) => {
          console.error(error);
          setShowError(true);
          setText('Failed to add new feed type.');
        });
    }
  };

  const openDeleteModal = (index) => {
    setSelectedFeedTypeIndex(index);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedFeedTypeIndex(null);
  };

  const updateType = (index, type, checked) => {
    const newVendorTypes = [...vendorTypes];
    newVendorTypes[index] = { ...vendorTypes[index], visible: checked };
    setVendorTypes(newVendorTypes);
    updateVendorType(newVendorTypes);
  };

  const deleteType = (index) => {
    if (index !== null) {
      const deletedVendorType = vendorTypes[index];
      if (deletedVendorType && deletedVendorType.id) {
        deleteVendorType(deletedVendorType.id)
          .then(() => {
            const newVendorTypes = vendorTypes.filter((_, i) => i !== index);
            setVendorTypes(newVendorTypes);
            setSelectedFeedTypeIndex(null);
            setShowSuccess(true);
            setText('Successfully deleted the vendor type.');
            closeDeleteModal();
          })
          .catch((error) => {
            console.error(error);
            setShowError(true);
            setText('An error occurred while deleting the vendor type.');
          });
      } else {
        console.error("Vendor type not found at index ", index);
      }
    }
  };

  useEffect(() => {
    getVendorTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div id="vendortypes" className="mt-3 mx-10 mb-3 w-3/4 max-w-4xl shadow overflow-hidden sm:rounded-lg">
      <div className="p-2 flex justify-between bg-gray-400 dark:bg-stone-700">
        <div className="flex">
          <button
            onClick={addNewVendorType}
            className="bg-blue-600 hover:bg-blue-500 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white font-semibold px-2 rounded"
          >
            Add Vendor Type
          </button>
          <input
            type="text"
            value={newVendorType}
            maxLength="125"
            placeholder={"New Vendor Type"}
            onChange={(e) => setNewVendorType(e.target.value)}
            className="bg-white border-2 border-gray-400 rounded-md shadow-sm w-48 px-2 py-1 text-sm ml-2 dark:bg-stone-800 dark:text-gray-300 dark:border-stone-800"
          />
        </div>
      </div>
      <div className="max-h-[80vh] overflow-y-scroll">
        <table className="min-w-full">
          <thead className="bg-gray-500 dark:bg-stone-500 sticky top-0 z-50">
          <tr>
            <th className="px-2 py-3 w-full text-left text-xs font-medium text-white uppercase tracking-wider select-none">
              Vendor Types
            </th>
            <th className="px-2 py-3 w-10 text-left text-xs font-medium text-white uppercase tracking-wider select-none">
              {/* Visible */}
            </th>
            <th className="px-2 py-3 w-20 text-left text-xs font-medium text-white uppercase tracking-wider select-none">
              {/* Empty header */}
            </th>
          </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 dark:bg-stone-900 dark:divide-gray-600 dark:text-gray-300">
          {vendorTypes.map((vendorType, index) => (
            <tr key={index}>
              <td className="px-2 py-2 whitespace-nowrap select-none">
                <span>{vendorType.type}</span>
              </td>
              <td className="px-2 py-2 whitespace-nowrap select-none z-10">
                <label
                  className={`switch inline-flex items-center w-20 px-1 rounded-md cursor-pointer select-none ${
                    vendorType.visible ? "bg-blue-400 dark:bg-indigo-500" : "bg-gray-200 dark:bg-gray-500"
                  }`}
                >
                  <input
                    className="hidden"
                    type="checkbox"
                    checked={vendorType.visible}
                    onChange={() =>
                      updateType(index, vendorType.type, !vendorType.visible)
                    }
                  />
                  <span
                    className={`block w-4 h-4 bg-white rounded-full shadow-inner transition-transform duration-200 ease-in-out select-none ${
                      vendorType.visible ? "transform translate-x-14" : ""
                    }`}
                  ></span>
                  <span
                    className={`text-xs ${
                      vendorType.visible ? "text-gray-100" : "text-gray-400"
                    }`}
                  >
                      {vendorType.visible ? "Visible" : "Hidden"}
                    </span>
                </label>
              </td>
              <td className="px-2 py-2 whitespace-nowrap">
                <button
                  onClick={() => openDeleteModal(index)}
                  className="text-red-400 hover:text-red-500 font-semibold px-2 py-1 rounded"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                    />
                  </svg>
                </button>
              </td>
            </tr>
          ))}
          </tbody>
        </table>
        {showDeleteModal && (
          <DeleteModal
            title="Are you sure?"
            onCancel={closeDeleteModal}
            onDelete={() => deleteType(selectedFeedTypeIndex)}
          ></DeleteModal>
        )}
        {showSuccess && (
          <SuccessNotification show={showSuccess} onClose={closeNotification} text={text}/>
        )}
        {showError && (
          <ErrorNotification show={showError} onClose={closeNotification} text={text}/>
        )}
      </div>
    </div>
  );
};

export default VendorTypes;
